import Button from "@/design-system/button";
import ReadMoreText from "@/design-system/read-more-text";
import { type ITelemetryCustom, sendTelemetryEvent } from "@/lib/telemetry";
import { languageSelector } from "@/src/contexts";
import LoginResources from "@/src/modals/login-registration/Login.Resources";
import LoginResourcesEn from "@/src/modals/login-registration/Login.Resources.En";
import { type ISettingsResources, SettingsResources } from "@/src/settings/Settings.Resources";
import { SettingsResourcesEn } from "@/src/settings/Settings.Resources.En";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import { Loading } from "@Components/Loading";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType } from "@Components/Modules/ErrorHandler";
import { isMailValid, isNullOrEmpty } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import { Textbox, TextboxType } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";
import type { IRadioButtonList } from "@Layout/RadioButtonList";
import { RadioButtonList } from "@Layout/RadioButtonList";
import * as stylex from "@stylexjs/stylex";

import * as cssPortal from "../shared/Portal.module.css";
import * as css from "./NewsletterSettings.module.css";

type NewsletterSettingsResponse = {
    success: boolean;
    longDescription?: string;
    shortDescription?: string;
    id?: string;
};

export interface INewsletterSettings extends IComponentAjax<NewsletterSettingsResponse> {
    newsletterLabel: string;
    newsletterRadioList: IRadioButtonList;
    newsletterEmailTextbox: string;

    emailRequiredError: IValidator;
    emailInvalidError: IValidator;
    emailInvalidLength: IValidator;
    newsletterRequiredError: IValidator;
    showSettingsSuccess: (message: string) => void;

    resources: ISettingsResources;
}

interface INewsletterSettingsState extends IComponentAjaxState {
    newsletter: boolean | null;
    email?: string;
    data: NewsletterSettingsResponse;
    defaultEmail?: string;
}

class NewsletterSettings extends ComponentAjax<
    NewsletterSettingsResponse,
    INewsletterSettings,
    INewsletterSettingsState
> {
    private get newsletterOptions() {
        const newsletterEmailTextbox: ITextbox = {
            onChange: (event) => this.setState((s) => ({ ...s, email: event.target.value })),
            value: this.state.email,
            textboxType: TextboxType.Email,
            clientId: "newsletter-email",
            maxLength: 250,
            onFocus: () => {
                sendTelemetryEvent({
                    eventName: "usprfl-newsletterEmailInput",
                    actionType: "Click",
                });
            },
        };

        if (this.state.newsletter === false) {
            return null;
        }

        return (
            <div className={css.wider}>
                <label htmlFor="newsletter-email-input" className={css.label}>
                    {this.props.resources.email}
                    <span className={cssPortal.required}>*</span>
                </label>
                <div className={css.settingRow}>
                    <Textbox {...newsletterEmailTextbox} ref="email" dataTestId="prof-news-email" />
                    <ErrorContainer
                        {...this.errors.emailRequiredError.container}
                        dataTestId="prof-news-valid-mess"
                    />
                    <ErrorContainer
                        {...this.errors.emailInvalidError.container}
                        dataTestId="prof-news-valid-mess"
                    />
                    <ErrorContainer
                        {...this.errors.emailInvalidLength.container}
                        dataTestId="prof-news-valid-mess"
                    />
                </div>
            </div>
        );
    }

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        emailRequiredError: {
            name: "email-required",
            type: ErrorType.Validator,
            targetRef: "email",
            container: { ref: "email-required-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.email) === false) {
                    return true;
                }
                return false;
            },
        },
        emailInvalidError: {
            name: "email-invalid",
            type: ErrorType.Validator,
            targetRef: "email",
            container: { ref: "email-invalid-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.email) === true) {
                    return true;
                }

                if (isMailValid(this.state.email)) {
                    return true;
                }
                return false;
            },
        },
        emailInvalidLength: {
            name: "email-invalid-lenght",
            type: ErrorType.Validator,
            targetRef: "email",
            container: { ref: "email-invalid-lenght-container" },
            onValidate: () => {
                if (isMailValid(this.state.email) === false) {
                    return true;
                }

                if (isNullOrEmpty(this.state.email) === true) {
                    return true;
                }

                const emailAliasLength = (this.state.email && this.state.email.split("@")[0].length) || 0;
                const emailHostnameLength = (this.state.email && this.state.email.split("@")[1].length) || 0;
                if (
                    emailAliasLength + emailHostnameLength < 5 ||
                    emailAliasLength + emailHostnameLength > 100
                ) {
                    return false;
                }

                return true;
            },
        },
        newsletterRequiredError: {
            name: "newsletter-input",
            type: ErrorType.Validator,
            targetRef: "newsletter",
            container: { ref: "newsletter-container" },
            onValidate: () => {
                if (this.state.newsletter === null) {
                    return false;
                }

                return true;
            },
        },
    };

    constructor(props: INewsletterSettings) {
        super({ ...props, ajaxMethod: AjaxMethod.Post });

        this.state = {
            isLoading: false,
            newsletter: props.newsletterRadioList.initialData === "True",
            email: props.newsletterEmailTextbox,
            data: props.initialData,
            defaultEmail: props.newsletterEmailTextbox,
        };

        this.newsletterSettingsButtonClick = this.newsletterSettingsButtonClick.bind(this);
    }

    public newsletterSettingsButtonClick() {
        const params = {
            hasNotifications: this.state.newsletter,
            email: this.state.email,
            id: this.state?.data?.id,
        };

        const defaultEventData: ITelemetryCustom = {
            eventName: "common-save",
            actionType: "Click",
            param2: this.state.newsletter ? "1" : "0",
            param3: this.state.email !== this.state.defaultEmail ? "1" : "0",
            origin: "usprfl-profile-newsletter",
        };

        this.ajaxRequest(
            params,
            () => {
                this.setState((s) => ({
                    ...s,
                    isLoading: true,
                    defaultEmail: s.newsletter ? s.email : s.defaultEmail,
                }));
                sendTelemetryEvent({ ...defaultEventData, param1: "1" });
            },
            () => sendTelemetryEvent({ ...defaultEventData, param1: "0" }),
        );
    }

    public renderNewsletterRadio() {
        const newsletterRadioList: IRadioButtonList = {
            ...this.props.newsletterRadioList,
            groupName: this.props.newsletterRadioList.groupName,
            onChange: (event) => {
                const newsletter = event.target.value === "True";
                this.setState((s) => ({
                    ...s,
                    newsletter: newsletter,
                }));
                sendTelemetryEvent({
                    eventName: "usprfl-newsletterToggle",
                    actionType: "Click",
                    param1: newsletter ? "1" : "0",
                });
                this.errors.emailRequiredError.disabled = newsletter === false;
                this.errors.emailInvalidError.disabled = newsletter === false;
                this.errors.emailInvalidLength.disabled = newsletter === false;
                this.errors.newsletterRequiredError.disabled = newsletter === false;
            },
        };

        const resources = languageSelector(LoginResources, LoginResourcesEn);

        return (
            <div className={css.settingRow}>
                {this.state.data?.shortDescription ? (
                    <ReadMoreText
                        longButtonText={resources.showLess}
                        shortButtonText={resources.showMore}
                        startShort
                        longDescription={this.state.data.longDescription}
                        shortDescription={this.state.data.shortDescription}
                        dataTestId="prof-news-desc"
                    />
                ) : (
                    <label className={css.label} data-testid="prof-news-desc">
                        {this.props.newsletterLabel}
                        <span className={cssPortal.required}>*</span>
                    </label>
                )}

                <div>
                    <RadioButtonList {...newsletterRadioList} ref="newsletter" />
                    <ErrorContainer {...this.errors.newsletterRequiredError.container} />
                </div>
            </div>
        );
    }

    public render() {
        return (
            <div {...this.rootDefaultAttributes(css.newsletterSettingsComponent)}>
                {this.renderNewsletterRadio()}

                {this.newsletterOptions}

                <Button
                    as="button"
                    size="M"
                    variant="magentaRound"
                    onClick={this.newsletterSettingsButtonClick}
                    data-testid="prof-news-save"
                    styleX={styles.saveBtn}
                >
                    {this.props.resources.saveChanges}
                </Button>
                {this.state.isLoading && <Loading isModal={true} />}
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected isSuccessNewsletterSettingsResponse(obj: unknown): obj is NewsletterSettingsResponse {
        return obj != null && typeof obj === "object" && "success" in obj;
    }

    protected ajaxSuccess(response: NewsletterSettingsResponse): void {
        if (this.isSuccessNewsletterSettingsResponse(response) === false) {
            this.setState((s) => ({ ...s, isLoading: false }));
            return;
        }

        const message = languageSelector(
            "Zmiany zostały zapisane - zaktualizujemy ustawienia w ciągu maksymalnie 24 godzin",
            "Changes saved - we will update the settings within a maximum of 24 hours",
        );

        sendTelemetryEvent({
            eventName: "NewsletterSettingsUpdateAttempt",
            actionType: null,
            param1: response.success ? "1" : "0",
        });

        if (this.props.showSettingsSuccess) {
            this.props.showSettingsSuccess(message);
        }

        this.setState((s) => ({ ...s, isLoading: false, data: response, email: s.defaultEmail }));
    }

    protected ajaxFail(): boolean {
        this.setState((s) => ({ ...s, isLoading: false }));
        sendTelemetryEvent({
            eventName: "NewsletterSettingsUpdateAttempt",
            actionType: null,
            param1: "0",
        });
        return true;
    }
}
const NewsletterSettingsConnect = (props: INewsletterSettings) => {
    const resources = languageSelector(SettingsResources, SettingsResourcesEn);

    return <NewsletterSettings {...props} ajaxMethod={AjaxMethod.Post} resources={resources} />;
};

export { NewsletterSettingsConnect as NewsletterSettings };

const styles = stylex.create({
    saveBtn: {
        width: "100%",
    },
});
